import { Box, ButtonBase, Typography } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useIsSidekickEnabled } from '../../flag';
import { useSidekickConsentGate } from '../../hooks/useSidekickConsentGate';
import { useSidekickIconState } from '../../hooks/useSidekickIconState';
import {
  CONSENT_STATUS,
  selectIsConsentGranted,
} from '../../state/aiSidekickConsentSlice';
import {
  closePanel,
  openPanel,
  requestInputBarFocus,
  selectChip,
  selectIsPanelOpen,
  selectSelectedChipId,
  togglePanel,
} from '../../state/aiSidekickSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import SidekickDotsIcon from '../icons/SidekickDotsIcon';
import SuggestionChip from '../SuggestionChip';
import SuggestionChipSkeleton from '../SuggestionChipSkeleton';
import { showSidekickErrorToast } from '../toast';
import useStyles from './SidekickSuggestionsCard.styles';
import type { SidekickSuggestionsCardProps } from './types';

const DEFAULT_LOADING_CHIP_COUNT = 4;

/**
 * Sidekick entry-point card: assistant header + suggestion chips.
 *
 * Reads and dispatches panel-open + chip-selected state from the
 * package's `aiSidekickSlice`. Returns `null` when the Sidekick
 * feature flag is off so consumers don't need to guard the render
 * site. Chip copy and the icon aria-label both resolve against the
 * package's own `SIDEKICK_TRANSLATION_NAMESPACE`.
 */
function SidekickSuggestionsCard({
  chips,
  onChipSelect,
  isLoading = false,
  loadingChipCount = DEFAULT_LOADING_CHIP_COUNT,
}: SidekickSuggestionsCardProps) {
  const { classes } = useStyles();
  const { t: tPackage } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const selectedChipId = useSelector(selectSelectedChipId);
  const isSidekickEnabled = useIsSidekickEnabled();
  const isConsentGranted = useSelector(selectIsConsentGranted);
  const { requestOpen, status } = useSidekickConsentGate();
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const isConsentCheckLoading = status === CONSENT_STATUS.CHECKING;
  // Mirror the in-chat composer icon so both brand marks animate in
  // lockstep (thinking while streaming, listening while the composer is
  // engaged, standby otherwise). Only while the panel is OPEN, though:
  // the persistent drawer keeps the composer mounted (and auto-focused)
  // even when closed, so an un-gated mirror would strand the entry icon
  // in `listening` on page load. When the panel is closed the in-chat
  // icon is off-screen, so the entry card rests at `standby`.
  const syncedIconState = useSidekickIconState();
  const iconState = isPanelOpen ? syncedIconState : 'standby';

  const showConsentErrorToast = useCallback(() => {
    // The consent hook stays presentation-free; the entry card owns the
    // error toast when the status check fails (package convention).
    showSidekickErrorToast({
      message: tPackage('chat.error.network.toast'),
      closeAriaLabel: tPackage('chat.error.network.toastDismissAriaLabel'),
      testId: 'ai-sidekick-entry-error-toast',
    });
  }, [tPackage]);

  const handleChipClick = useCallback(
    (chipId: string) => {
      // Until consent is known this session, route the open-intent through
      // the consent check. The chip's selection + host submission are
      // replayed only once the check finds prior consent (returning user);
      // a first-time user just sees the consent modal with nothing
      // submitted.
      if (!isConsentGranted) {
        void requestOpen({
          onGranted: () => {
            dispatch(selectChip(chipId));
            onChipSelect?.(chipId);
            dispatch(requestInputBarFocus());
          },
        }).then((outcome) => {
          if (outcome === 'error') {
            showConsentErrorToast();
            iconButtonRef.current?.focus();
          }
        });
        return;
      }
      dispatch(selectChip(chipId));
      dispatch(openPanel());
      onChipSelect?.(chipId);
    },
    [
      dispatch,
      onChipSelect,
      isConsentGranted,
      requestOpen,
      showConsentErrorToast,
    ]
  );

  const handleIconClick = useCallback(() => {
    if (isConsentCheckLoading) {
      return;
    }
    if (!isConsentGranted) {
      // Open-intent from the brand icon runs the consent check; when the
      // panel is already open (e.g. showing the consent modal) the icon
      // collapses it instead.
      if (isPanelOpen) {
        dispatch(closePanel());
      } else {
        void requestOpen({
          onGranted: () => dispatch(requestInputBarFocus()),
        }).then((outcome) => {
          if (outcome === 'error') {
            showConsentErrorToast();
          }
        });
      }
      return;
    }
    dispatch(togglePanel());
  }, [
    dispatch,
    isConsentGranted,
    isPanelOpen,
    isConsentCheckLoading,
    requestOpen,
    showConsentErrorToast,
  ]);

  const chipClickById = useMemo(
    () =>
      Object.fromEntries(
        chips.map((c) => [c.id, () => handleChipClick(c.id)] as const)
      ) as Record<string, () => void>,
    [chips, handleChipClick]
  );

  const skeletonKeys = useMemo(
    () =>
      Array.from(
        { length: Math.max(0, loadingChipCount) },
        (_, index) => `chip-skeleton-${index}`
      ),
    [loadingChipCount]
  );
  const showLoadingChips = isLoading || isConsentCheckLoading;

  if (!isSidekickEnabled) {
    return null;
  }

  const iconAriaLabel = String(
    tPackage(isPanelOpen ? 'panel.closeAriaLabel' : 'entry.openAriaLabel')
  );

  return (
    <Box
      className={classes.card}
      data-testid="ai-sidekick-suggestions-card"
      data-state={isPanelOpen ? 'open' : 'closed'}
      aria-busy={showLoadingChips}
    >
      <Box className={classes.header} data-testid="ai-sidekick-entry-header">
        <ButtonBase
          ref={iconButtonRef}
          type="button"
          className={classes.iconSlot}
          aria-label={iconAriaLabel}
          aria-expanded={isPanelOpen}
          aria-disabled={isConsentCheckLoading}
          onClick={handleIconClick}
        >
          <SidekickDotsIcon state={iconState} decorative size={16} />
        </ButtonBase>
        <Typography className={classes.title}>
          {tPackage('panel.title')}
        </Typography>
      </Box>
      <Box className={classes.chipsRow} data-testid="ai-sidekick-chip-row">
        {showLoadingChips
          ? skeletonKeys.map((key) => <SuggestionChipSkeleton key={key} />)
          : chips.map((chip) => {
              const chipLabel =
                chip.label !== undefined
                  ? chip.label
                  : tPackage(chip.translationKey);
              return (
                <SuggestionChip
                  key={chip.id}
                  id={chip.id}
                  label={chipLabel}
                  selected={selectedChipId === chip.id}
                  onClick={chipClickById[chip.id]}
                />
              );
            })}
      </Box>
    </Box>
  );
}

export default SidekickSuggestionsCard;
